import React from "react";
import { IResourceComponentsProps, useTranslate } from "@refinedev/core";
import { Edit, NumberField, useForm, useSelect } from "@refinedev/antd";
import { Form, Input, DatePicker, Select, InputNumber, Card, Space, Typography, Tooltip, Row, Col, Radio } from "antd";
import { InfoCircleOutlined } from "@ant-design/icons";
import dayjs from "dayjs";

const { Title } = Typography;

export const ProductEdit: React.FC<IResourceComponentsProps> = () => {
    const translate = useTranslate();
    const { formProps, saveButtonProps, queryResult } = useForm();

    const productsData = queryResult?.data?.data;

    const { selectProps } = useSelect({
        resource: "forms",
        optionValue: "id",
        optionLabel: "id",
        defaultValue: productsData?.form?.id,
    });

    return (
        <Edit saveButtonProps={saveButtonProps}>
            <Form {...formProps} layout="vertical">
                <Space direction="vertical" size="large" style={{ width: '100%' }}>
                    <Card>
                        <Title level={5}>{translate("products.fields.basicInfo")}</Title>
                        <Row gutter={[24, 24]}>
                            <Col xs={24} sm={12} md={12}>
                                <Form.Item
                                    label={
                                        <Space>
                                            {translate("products.fields.name")}
                                            <Tooltip title={translate("products.fields.tooltips.name")}>
                                                <InfoCircleOutlined />
                                            </Tooltip>
                                        </Space>
                                    }
                                    name={["name"]}
                                    rules={[
                                        {
                                            required: true,
                                            message: translate("fields.errors.required"),
                                        }
                                    ]}
                                >
                                    <Input placeholder={translate("products.fields.placeholders.name")} />
                                </Form.Item>
                            </Col>
                            <Col xs={24} sm={12} md={8}>
                                <Form.Item
                                    label={
                                        <Space>
                                            {translate("products.fields.code")}
                                            <Tooltip title={translate("products.fields.tooltips.code")}>
                                                <InfoCircleOutlined />
                                            </Tooltip>
                                        </Space>
                                    }
                                    name={["code"]}
                                    rules={[
                                        {
                                            required: true,
                                            message: translate("fields.errors.required"),
                                        }
                                    ]}
                                >
                                    <Input placeholder={translate("products.fields.placeholders.code")} />
                                </Form.Item>
                            </Col>
                            <Col xs={24} sm={12} md={8}>
                                <Form.Item
                                    label={
                                        <Space>
                                            {translate("products.fields.type")}
                                            <Tooltip title={translate("products.fields.tooltips.type")}>
                                                <InfoCircleOutlined />
                                            </Tooltip>
                                        </Space>
                                    }
                                    name={["type"]}
                                    rules={[
                                        {
                                            required: true,
                                            message: translate("fields.errors.required"),
                                        },
                                    ]}
                                >
                                    <Radio.Group>
                                        <Radio value="base">{translate("products.fields.typeValues.base")}</Radio>
                                        <Radio value="option">{translate("products.fields.typeValues.option")}</Radio>
                                    </Radio.Group>
                                </Form.Item>
                            </Col>
                        </Row>
                    </Card>

                    <Card>
                        <Title level={5}>{translate("products.fields.pricingDetails")}</Title>
                        <Row gutter={[24, 24]}>
                            <Col xs={24} sm={8}>
                                <Form.Item
                                    label={
                                        <Space>
                                            {translate("products.fields.price")}
                                            <Tooltip title={translate("products.fields.tooltips.price")}>
                                                <InfoCircleOutlined />
                                            </Tooltip>
                                        </Space>
                                    }
                                    name={["price"]}
                                >
                                    <InputNumber
                                        min={0}
                                        stringMode
                                        precision={0}
                                        placeholder={translate("products.fields.placeholders.price")}
                                        style={{ width: '100%' }}
                                        addonBefore="Crédit"
                                    />
                                </Form.Item>
                            </Col>
                        </Row>
                        <Row gutter={[24, 24]}>
                            <Col xs={24} sm={24} md={16}>
                                <Form.Item
                                    label={
                                        <Space>
                                            {translate("products.fields.description")}
                                            <Tooltip title={translate("products.fields.tooltips.description")}>
                                                <InfoCircleOutlined />
                                            </Tooltip>
                                        </Space>
                                    }
                                    name={["description"]}
                                    rules={[
                                        {
                                            max: 500,
                                            message: translate("fields.errors.maxLength", { max: 500 }),
                                        },
                                    ]}
                                >
                                    <Input.TextArea
                                        placeholder={translate("products.fields.placeholders.description")}
                                        rows={4}
                                    />
                                </Form.Item>
                            </Col>
                        </Row>
                    </Card>

                    <Card>
                        <Title level={5}>{translate("products.fields.classification")}</Title>
                        <Row gutter={[24, 24]}>
                            <Col xs={24} sm={12} md={8}>
                                <Form.Item
                                    label={
                                        <Space>
                                            {translate("products.fields.group")}
                                            <Tooltip title={translate("products.fields.tooltips.group")}>
                                                <InfoCircleOutlined />
                                            </Tooltip>
                                        </Space>
                                    }
                                    name={["group"]}
                                    rules={[
                                        {
                                            max: 100,
                                            message: translate("fields.errors.maxLength", { max: 100 }),
                                        },
                                    ]}
                                >
                                    <Input placeholder={translate("products.fields.placeholders.group")} />
                                </Form.Item>
                            </Col>
                            <Col xs={24} sm={24} md={16}>
                                <Form.Item
                                    label={
                                        <Space>
                                            {translate("products.fields.tags")}
                                            <Tooltip title={translate("products.fields.tooltips.tags")}>
                                                <InfoCircleOutlined />
                                            </Tooltip>
                                        </Space>
                                    }
                                    name={["tags"]}
                                >
                                    <Select
                                        mode="tags"
                                        placeholder={translate("products.fields.placeholders.tags")}
                                        tokenSeparators={[","]}
                                        style={{ width: "100%" }}
                                    />
                                </Form.Item>
                            </Col>
                        </Row>

                        <Form.Item
                            label={translate("products.fields.form")}
                            name={"formId"}
                            initialValue={formProps?.initialValues?.form?.id}
                            hidden
                            rules={[
                                {
                                    required: false,
                                },
                            ]}
                        >
                            <Select {...selectProps} />
                        </Form.Item>
                    </Card>
                </Space>
            </Form>
        </Edit>
    );
};
